import { Text } from '@react-spectrum/s2';
import Checkmark from '@react-spectrum/s2/icons/Checkmark';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo } from 'react';

import { cn } from '@/utils/cn';

interface ArtifactStepperProps {
	labels: string[];
	currentIndex: number;
}

const baseLabelStyles = {
	font: 'detail',
	fontSize: 'detail-sm',
} as const;

const labelStyles = style({
	...baseLabelStyles,
	color: 'detail',
});

const currentLabelStyles = style({
	...baseLabelStyles,
	color: 'body',
	fontWeight: 'bold',
});

export const ArtifactStepper = memo(function ArtifactStepper({
	labels,
	currentIndex,
}: ArtifactStepperProps) {
	return (
		<div className="flex w-full shrink-0 items-start overflow-x-auto px-6 py-4">
			{labels.map((label, i) => {
				const isCompleted = i < currentIndex;
				const isCurrent = i === currentIndex;

				return (
					// `items-stretch`, not `items-center`. Centring makes the label span a
					// fit-content flex item, and `truncate` sets `nowrap`, so its min-content width
					// IS the whole string: the label kept full width, spilled sideways over its
					// neighbours, and no ellipsis ever appeared. Stretched, the span fills the cell
					// and `truncate` clips as intended. `text-center` keeps the centring.
					<div key={i} className="flex min-w-0 flex-1 flex-col items-stretch">
						<Text styles={isCurrent ? currentLabelStyles : labelStyles}>
							<span
								// A cell is (panel - 96px)/N wide, so a label can legitimately clip.
								// The title is the only way back to the full word.
								title={label}
								className="mb-1.5 block truncate text-center"
							>
								{label}
							</span>
						</Text>

						<div className="flex w-full items-center">
							{i === 0 ? (
								<div className="flex-1" />
							) : (
								<div
									className={cn(
										'h-[2px] flex-1',
										i <= currentIndex
											? 'bg-gray-400'
											: 'bg-[var(--glass-border)]',
									)}
								/>
							)}

							<div className="flex size-5 shrink-0 items-center justify-center">
								{isCompleted ? (
									<Checkmark aria-hidden />
								) : (
									<div
										className={cn(
											'size-3 rounded-full',
											isCurrent
												? 'bg-gray-800'
												: 'border-2 border-[var(--glass-border)] bg-transparent',
										)}
									/>
								)}
							</div>

							{i === labels.length - 1 ? (
								<div className="flex-1" />
							) : (
								<div
									className={cn(
										'h-[2px] flex-1',
										i < currentIndex
											? 'bg-gray-400'
											: 'bg-[var(--glass-border)]',
									)}
								/>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
});
